// src/pages/Vouchers/VoucherPage.js
//
// Voucher create/edit page (backoffice).
//
// GOALS
// -----
// 1) Two-step flow:
//    - Step 1: choose category + generate voucher number (Draft on backend).
//    - Step 2: fill in all details and finalize (Active / updated).
//
// 2) DB-aligned fields in payload (Sequelize-friendly):
//    - buyer_name, recipient_name, buyer_phone
//    - value, currency
//    - template_type, template_version, theme
//
// 3) Layout metadata dropdowns:
//    - Template Type: A4_ON_STATIONERY / A4_FULL / A4_PURE_CARD / A4_ENVELOPE / A4_COMBINED
//    - Template Version: split by business mode
//    - Theme: normalized through the correct config family:
//        * voucherLayoutConfig for vouchers
//        * packageLayoutConfig for packages
//
// 4) Safety & UX:
//    - Idempotent number generation: backend can reuse draft if Idempotency-Key repeats.
//    - Navigation blocker while a Draft exists and is not finalized.
//
// PACKAGE-SPECIFIC UX UPDATE
// --------------------------
// This page supports category "Pakiet" in a business-correct way.
//
// Package-specific business rules:
//
// 1) Dynamic wording
//    - whenever category === "Pakiet", user-facing labels that normally contain
//      the word "Voucher" switch to "Package"
//
// 2) Recipient is required for packages
//    - when category === "Pakiet", recipient_name becomes mandatory
//    - this is enforced both:
//        a) in frontend validation before submit
//        b) in the HTML field itself via `required`
//
// 3) Package layout profile is NO LONGER borrowed from voucher config
//    - package mode now uses packageLayoutConfig.js
//    - voucher mode uses voucherLayoutConfig.js
//
// NEW FRONTEND SPLIT DECISION
// ---------------------------
// This file is one of the key frontend places where the architectural split
// becomes real.
//
// Before:
//   VoucherPage used voucherLayoutConfig concepts even when handling packages.
//
// Now:
//   VoucherPage explicitly switches between:
//
//     - voucher profile
//     - package profile
//
// depending on category/businessMode.
//
// This matches the backend direction:
//
//   backend/pdf/templates/voucher/*
//   backend/pdf/templates/package/*
//
// IMPORTANT DESIGN DECISION
// -------------------------
// We intentionally separate:
//
//   1) TECHNICAL persistence contract
//      - template_type
//      - template_version
//      - theme
//
//   2) BUSINESS UI profile
//      - voucher profile
//      - package profile
//
// This is pragmatic because:
//   - backend contract stays stable
//   - frontend UX becomes clearer
//   - future expansion becomes much easier
//
// That means:
//   - database still stores the row in vouchers table
//   - backend still receives the same field names
//   - only the UI profile and defaults change when category is Pakiet
//
// NOTES
// -----
// - UI texts are in English (internal backoffice).
// - Navigation protection is handled via useNavigationBlocker:
//      VoucherPage controls `isBlocking`
// - We intentionally do not block navigation for the "edit existing voucher" flow
//   (unless you explicitly want "unsaved changes" protection there too).
//

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { fetchUsers } from "../../services/userService";
import {
  fetchNextVoucherNumber,
  updateVoucher,
  cancelVoucher,
  genIdempotencyKey,
} from "../../services/voucherService";

import {
  voucherLayoutConfig,
  VOUCHER_THEMES,
  normalizeTheme as normalizeVoucherTheme,
  templateVersionToThemeMap as voucherTemplateVersionToThemeMap,
} from "../../config/voucherLayoutConfig";

import {
  packageLayoutConfig,
  PACKAGE_THEMES,
  normalizeTheme as normalizePackageTheme,
  templateVersionToThemeMap as packageTemplateVersionToThemeMap,
} from "../../config/packageLayoutConfig";

import useNavigationBlocker from "../../hooks/useNavigationBlocker";
import "./VoucherPage.css";

/* -----------------------------------------------------------------------------
   CONFIG: allowed categories for Step 1
   -----------------------------------------------------------------------------
//
// IMPORTANT
// ---------
// "Pakiet" is intentionally included here as a business category that still
// persists in the shared vouchers domain/table.
//
// This page handles Pakiet as a specialized package flow living inside the
// broader voucher-domain CRUD screen.
*/
const ALLOWED_CATEGORIES = ["Karta", "Gotówka", "Zero", "Pakiet"];

/* -----------------------------------------------------------------------------
   BUSINESS MODES
   -----------------------------------------------------------------------------
//
// We explicitly separate two UI/business modes:
//
//   - voucher mode
//   - package mode
//
// This is NOT DB schema separation.
// It is a frontend routing/configuration layer that decides:
//
//   - wording
//   - default template version
//   - default theme
//   - template-version options
//   - theme registry
//   - hints / legends
//
// This gives us a much cleaner path for future expansion.
*/
const BUSINESS_MODES = Object.freeze({
  VOUCHER: "voucher",
  PACKAGE: "package",
});

/* -----------------------------------------------------------------------------
   CONFIG: template type options
   -----------------------------------------------------------------------------
//
// Template type remains technically shared between vouchers and packages.
//
// Why shared?
// - it describes the PDF/page structure,
// - not the business motif catalog itself.
//
// IMPORTANT ALIGNMENT UPDATE
// --------------------------
// The backend router already supports:
//
//   - A4_ON_STATIONERY
//   - A4_FULL
//   - A4_PURE_CARD
//   - A4_ENVELOPE
//   - A4_COMBINED
//
// So the frontend selector should expose the same canonical template_type list.
//
*/

const TEMPLATE_TYPE_OPTIONS = [
  {
    value: "A4_ON_STATIONERY",
    label: "A4 on stationery (standard)",
    hint: "Classic company-branded print on A4 stationery.",
  },
  {
    value: "A4_FULL",
    label: "A4 full-page layout",
    hint: "Full A4 page used entirely as the printable surface.",
  },
  {
    value: "A4_PURE_CARD",
    label: "A4 pure card",
    hint: "Core card content only, without extra header/footer framing.",
  },
  {
    value: "A4_ENVELOPE",
    label: "Envelope format",
    hint: "Smaller or fold-friendly format for elegant envelope usage.",
  },
  {
    value: "A4_COMBINED",
    label: "Set: A4 + envelope",
    hint: "Customer receives both versions in one order.",
  },
];

/* -----------------------------------------------------------------------------
   CONFIG: voucher profile
   -----------------------------------------------------------------------------
//
// These are the traditional voucher-business options.
//
// You can rename/add/remove them later without changing the underlying
// backend field names.
*/
const VOUCHER_TEMPLATE_VERSION_OPTIONS = [
  { value: "company", label: "Company (classic)" },
  { value: "christmas", label: "Christmas" },
  { value: "valentine", label: "Valentine" },
];

/* -----------------------------------------------------------------------------
   CONFIG: package profile
   -----------------------------------------------------------------------------
//
// IMPORTANT
// ---------
// These are intentionally separate from voucher options.
//
// They represent a DIFFERENT business catalog for package motif/version choices.
// The actual values saved to DB are still plain strings in template_version.
//
// You can now freely decide what package-specific versions should exist,
// without being coupled to voucher motifs.
*/
const PACKAGE_TEMPLATE_VERSION_OPTIONS = [
  { value: "package_classic", label: "Package Classic" },
  { value: "package_premium", label: "Package Premium" },
  { value: "package_gold", label: "Package Gold" },
];

/* -----------------------------------------------------------------------------
   CONFIG: defaults and registries by business mode
   -----------------------------------------------------------------------------
//
// This is one of the most important improvements in the refactor.
//
// Instead of one global:
//
//   DEFAULT_TEMPLATE_VERSION
//   DEFAULT_THEME
//   one global templateVersion -> theme map
//   one global normalizeTheme function
//
// we now have business-profile-aware configuration.
//
// This makes "Pakiet" behave like a proper first-class business mode.
*/
const PROFILE_CONFIG = Object.freeze({
  [BUSINESS_MODES.VOUCHER]: {
    templateTypeDefault: "A4_ON_STATIONERY",
    templateVersionDefault: "company",
    themeDefault: voucherLayoutConfig.defaultThemeKey,
    templateVersionOptions: VOUCHER_TEMPLATE_VERSION_OPTIONS,
    themeRegistry: VOUCHER_THEMES,
    normalizeThemeFn: normalizeVoucherTheme,
    templateVersionToThemeMap: voucherTemplateVersionToThemeMap,

    layoutLegend: "Voucher layout & design",
    templateVersionLabel: "Template version (motif):",
    templateVersionHint:
      "Matches voucher/shop motifs: classic / Christmas / Valentine.",
    templateTypeHint:
      "Controls PDF layout (A4 stationery / pure card / envelope / combined / full-page).",
    themeLabel: "Theme (color palette):",
    themeHint:
      "Advanced override. By default it follows voucher template version.",
  },

  [BUSINESS_MODES.PACKAGE]: {
    templateTypeDefault: "A4_ON_STATIONERY",
    templateVersionDefault: "package_classic",
    themeDefault: packageLayoutConfig.defaultThemeKey,
    templateVersionOptions: PACKAGE_TEMPLATE_VERSION_OPTIONS,
    themeRegistry: PACKAGE_THEMES,
    normalizeThemeFn: normalizePackageTheme,
    templateVersionToThemeMap: packageTemplateVersionToThemeMap,

    layoutLegend: "Package layout & design",
    templateVersionLabel: "Package motif:",
    templateVersionHint:
      "Choose the package motif family used for package presentation.",
    templateTypeHint:
      "Controls PDF layout (A4 stationery / pure card / envelope / combined / full-page) for this package.",
    themeLabel: "Package color theme:",
    themeHint:
      "Advanced override. By default it follows package motif.",
  },
});

/* -----------------------------------------------------------------------------
   Helpers: dates (avoid timezone off-by-one)
   -----------------------------------------------------------------------------
//
// Why not `toISOString().split("T")[0]`?
// - That uses UTC. In non-UTC timezones, you can get the previous/next day.
//
// Our approach:
// - Format local date => YYYY-MM-DD for <input type="date">.
// - If backend returns already YYYY-MM-DD, keep it as-is.
*/
function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDateForInputLocal(dateObj) {
  const y = dateObj.getFullYear();
  const m = pad2(dateObj.getMonth() + 1);
  const d = pad2(dateObj.getDate());
  return `${y}-${m}-${d}`;
}

function normalizeDateToInput(value, fallback) {
  if (!value) return fallback;

  // Already in "YYYY-MM-DD"
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return formatDateForInputLocal(d);
}

function addDaysLocal(baseDate, days) {
  const d = new Date(baseDate);
  d.setDate(d.getDate() + days);
  return d;
}

/* -----------------------------------------------------------------------------
   Helper: resolve business mode from category
   -----------------------------------------------------------------------------
//
// Category "Pakiet" => package mode
// everything else   => voucher mode
//
// This function centralizes the decision instead of spreading string checks
// around the component.
*/
function getBusinessModeFromCategory(category) {
  return category === "Pakiet"
    ? BUSINESS_MODES.PACKAGE
    : BUSINESS_MODES.VOUCHER;
}

/* -----------------------------------------------------------------------------
   Helper: get config for a business mode
   ----------------------------------------------------------------------------- */
function getProfileConfig(mode) {
  return PROFILE_CONFIG[mode] || PROFILE_CONFIG[BUSINESS_MODES.VOUCHER];
}

/* -----------------------------------------------------------------------------
   Helper: normalize theme through the CORRECT family config
   -----------------------------------------------------------------------------
//
// IMPORTANT
// ---------
// This is one of the key frontend split changes.
//
// Before:
//   package mode could still accidentally be normalized via voucher config.
//
// Now:
//   - voucher mode uses voucherLayoutConfig normalization
//   - package mode uses packageLayoutConfig normalization
//
// Even though current canonical theme keys are still shared, the ownership
// of the config is no longer shared.
*/
function normalizeThemeForMode(theme, mode) {
  const profile = getProfileConfig(mode);
  const normalizeFn = profile.normalizeThemeFn || normalizeVoucherTheme;
  return normalizeFn(theme);
}

/* -----------------------------------------------------------------------------
   Helper: normalize template_type loaded from DB / UI
   -----------------------------------------------------------------------------
//
// We keep template_type technically shared across business families,
// but still normalize it centrally for safety.
//
// Fallback:
//   - profile default template type
*/
function normalizeTemplateType(rawType, mode) {
  const profile = getProfileConfig(mode);
  const fallback = profile.templateTypeDefault || "A4_ON_STATIONERY";

  if (!rawType) return fallback;

  const value = String(rawType).trim().toUpperCase();
  const known = TEMPLATE_TYPE_OPTIONS.some((opt) => opt.value === value);

  return known ? value : fallback;
}

/* -----------------------------------------------------------------------------
   Helper: normalize template_version loaded from DB
   -----------------------------------------------------------------------------
//
// We may still see legacy or unknown values in existing rows.
//
// IMPORTANT:
// normalization is now MODE-AWARE.
//
// Why?
// - voucher mode and package mode have different legal catalogs
// - fallback should go to the correct profile default
//
// Special legacy handling kept:
//   - "v1" -> "company" for voucher mode
*/
function normalizeTemplateVersion(rawVersion, mode) {
  const profile = getProfileConfig(mode);
  const knownOptions = profile.templateVersionOptions || [];
  const defaultVersion = profile.templateVersionDefault;

  if (!rawVersion) return defaultVersion;

  const v = String(rawVersion).trim().toLowerCase();

  if (mode === BUSINESS_MODES.VOUCHER && v === "v1") {
    return "company";
  }

  const known = knownOptions.some((opt) => opt.value === v);
  return known ? v : defaultVersion;
}

/* -----------------------------------------------------------------------------
   Helper: derive theme from templateVersion using the CORRECT family config
   -----------------------------------------------------------------------------
//
// We support two mapping layers:
//
// 1) Family-specific mapping:
//      profile.templateVersionToThemeMap[templateVersion]
//
// 2) Fallback normalization:
//      normalizeThemeForMode(templateVersion, mode)
//
// This means:
// - package motifs now use packageLayoutConfig mapping
// - voucher motifs use voucherLayoutConfig mapping
// - fallback behavior still stays resilient
*/
function deriveThemeFromTemplateVersion(version, mode) {
  const profile = getProfileConfig(mode);
  const map = profile.templateVersionToThemeMap || {};

  const normalizedVersion = String(version || "").trim().toLowerCase();
  const mapped = map[normalizedVersion];

  if (mapped) {
    return normalizeThemeForMode(mapped, mode);
  }

  // Conservative fallback:
  // if version itself does not resolve to a meaningful theme alias,
  // use mode default theme.
  return normalizeThemeForMode(
    normalizedVersion || profile.themeDefault,
    mode
  );
}

/* -----------------------------------------------------------------------------
   Helper: parse positive-ish validity days from env
   -----------------------------------------------------------------------------
//
// React env vars are build-time. We provide a sane fallback.
// If env is invalid, fallback to 90 days.
*/
function getValidityDays() {
  const raw = process.env.REACT_APP_VOUCHER_VALIDITY_DAYS;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 90;
  return Math.min(Math.max(Math.floor(n), 1), 3650);
}

const VoucherPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(["common", "vouchers"]);
  // i18n note:
  // English remains the source language and Polish is provided through the
  // vouchers namespace. The persisted category/type values stay unchanged
  // because backend APIs and existing data still expect the original codes.


  const backToOverviewPath =
  location.state?.returnTo || "/vouchers/overview";

  // "edit" | undefined (create)
  const { voucher, phase } = location.state || {};
  const isEdit = phase === "edit";

  // Ref to the page scroll container
  const pageRef = useRef(null);

  // Controls sticky styling on action bar when scrolling inside the card
  const [showSticky, setShowSticky] = useState(false);

  // Step & identity
  const [step, setStep] = useState(1);
  const [voucherId, setVoucherId] = useState(null);
  const [voucherNo, setVoucherNo] = useState("");
  const [voucherSearchNo, setVoucherSearchNo] = useState("");

  // Core fields
  const [category, setCategory] = useState("");
  const [issuerId, setIssuerId] = useState(""); // UI keeps string; payload sends number
  const [users, setUsers] = useState([]);
  const [type, setType] = useState("Usługa");

  // Person fields
  const [buyerName, setBuyerName] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");

  // Monetary / template meta
  const [value, setValue] = useState("");
  const [currency, setCurrency] = useState("PLN");

  // Layout metadata
  const [templateType, setTemplateType] = useState(
    getProfileConfig(BUSINESS_MODES.VOUCHER).templateTypeDefault
  );
  const [templateVersion, setTemplateVersion] = useState(
    getProfileConfig(BUSINESS_MODES.VOUCHER).templateVersionDefault
  );
  const [theme, setTheme] = useState(
    getProfileConfig(BUSINESS_MODES.VOUCHER).themeDefault
  );

  // Free text
  const [description, setDescription] = useState("");
  const [expirationDate, setExpirationDate] = useState("");

  // Flags / internal comment
  const [toNote, setToNote] = useState(false);
  const [comment, setComment] = useState("");

  // UX messages / flags
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Navigation blocker:
  // - true: warn when leaving (Draft created, not finalized)
  // - false: free navigation
  const [isBlocking, setIsBlocking] = useState(false);

  // Idempotency
  const [reused, setReused] = useState(false);
  const [idemKey, setIdemKey] = useState("");
  const [generating, setGenerating] = useState(false);

  /* -------------------------------------------------------------------------
     Derived flags: package mode
     -------------------------------------------------------------------------
     "Pakiet" behaves almost like voucher, but we want:
     - package wording in the UI
     - recipient required
     - separate metadata profile
  */
  const businessMode = useMemo(
    () => getBusinessModeFromCategory(category),
    [category]
  );

  const isPackageCategory = businessMode === BUSINESS_MODES.PACKAGE;

  const profileConfig = useMemo(
    () => getProfileConfig(businessMode),
    [businessMode]
  );

  /* -------------------------------------------------------------------------
     Derived user-facing wording
     -------------------------------------------------------------------------
     IMPORTANT:
     This remains intentionally a presentation-oriented abstraction.
     Backend payload field names do not change.
  */
  const uiText = useMemo(() => {
    const entityKey = isPackageCategory ? "package" : "voucher";
    const singular = t(`vouchers:entity.${entityKey}.singular`, {
      defaultValue: isPackageCategory ? "Package" : "Voucher",
    });
    const plural = t(`vouchers:entity.${entityKey}.plural`, {
      defaultValue: isPackageCategory ? "Packages" : "Vouchers",
    });
    const lowerSingular = t(`vouchers:entity.${entityKey}.lowerSingular`, {
      defaultValue: singular.toLowerCase(),
    });

    const i18nVars = { entity: singular, entityLower: lowerSingular };

    return {
      singular,
      plural,
      lowerSingular,

      // Page-level titles.
      // Translation keys are semantic, while interpolation preserves the
      // voucher/package wording split without duplicating the whole form.
      pageTitle: isEdit
        ? t("vouchers:voucherPage.pageTitleEdit", i18nVars)
        : t("vouchers:voucherPage.pageTitleCreate", i18nVars),
      step1Title: t("vouchers:voucherPage.step1Title", i18nVars),
      step2Title: t("vouchers:voucherPage.step2Title", i18nVars),

      // Step 1
      step1SelectLabel: t("vouchers:voucherPage.selectCategory"),
      categoryFieldAriaLabel: t("vouchers:voucherPage.categoryAriaLabel", i18nVars),
      step1GenerateButton: t("vouchers:voucherPage.generateNumber", i18nVars),
      generateInProgress: t("vouchers:voucherPage.generating", i18nVars),

      // Identity fields
      numberLabel: t("vouchers:voucherPage.numberLabel", i18nVars),
      searchNumberLabel: t("vouchers:voucherPage.searchNumberLabel", i18nVars),

      // Success / error / action wording
      generateSuccessNew: t("vouchers:voucherPage.generateSuccessNew", i18nVars),
      generateSuccessReused: t("vouchers:voucherPage.generateSuccessReused", i18nVars),
      generateError: t("vouchers:voucherPage.generateError", i18nVars),

      saveSuccess: t("vouchers:voucherPage.saveSuccess", i18nVars),
      saveError: t("vouchers:voucherPage.saveError", i18nVars),

      createButton: t("vouchers:voucherPage.createButton", i18nVars),
      saveChangesButton: t("vouchers:voucherPage.saveChanges"),
      cancelEntityButton: t("vouchers:voucherPage.cancelEntityButton", i18nVars),
      cancelEntityConfirm: t("vouchers:voucherPage.cancelEntityConfirm", i18nVars),
      cancelEntitySuccess: t("vouchers:voucherPage.cancelEntitySuccess", i18nVars),
      cancelEntityError: t("vouchers:voucherPage.cancelEntityError", i18nVars),

      missingIdError: t("vouchers:voucherPage.missingIdError", i18nVars),
      reusedInfoChip: t("vouchers:voucherPage.reusedInfoChip"),

      // Labels / placeholders / help texts
      buyerNameLabel: t("vouchers:voucherPage.buyerNameLabel"),
      buyerNamePlaceholder: t("vouchers:voucherPage.buyerNamePlaceholder", i18nVars),

      recipientNameLabel: t("vouchers:voucherPage.recipientNameLabel"),
      recipientNameRequiredError: t("vouchers:voucherPage.recipientNameRequiredError"),

      recipientNamePlaceholder: isPackageCategory
        ? t("vouchers:voucherPage.recipientNamePlaceholderPackage")
        : t("vouchers:voucherPage.recipientNamePlaceholderVoucher"),

      buyerPhoneLabel: t("vouchers:voucherPage.buyerPhoneLabel"),
      valueAriaLabel: t("vouchers:common.value"),

      expirationDateLabel: t("vouchers:voucherPage.expirationDateLabel"),
      categoryLabel: t("vouchers:voucherPage.categoryLabel"),
      issuerLabel: t("vouchers:voucherPage.issuerLabel"),
      typeLabel: t("vouchers:voucherPage.typeLabel"),
      templateTypeLabel: t("vouchers:voucherPage.templateTypeLabel"),
      selectIssuer: t("vouchers:common.selectIssuer"),
      selectPlaceholder: t("vouchers:common.select"),
      markToNote: t("vouchers:common.markToNote"),

      // Layout section now comes from business profile config keys.
      layoutLegend: t(`vouchers:profile.${entityKey}.layoutLegend`, {
        defaultValue: profileConfig.layoutLegend,
      }),
      templateVersionLabel: t(`vouchers:profile.${entityKey}.templateVersionLabel`, {
        defaultValue: profileConfig.templateVersionLabel,
      }),
      templateVersionHint: t(`vouchers:profile.${entityKey}.templateVersionHint`, {
        defaultValue: profileConfig.templateVersionHint,
      }),
      templateTypeHint: t(`vouchers:profile.${entityKey}.templateTypeHint`, {
        defaultValue: profileConfig.templateTypeHint,
      }),
      themeLabel: t(`vouchers:profile.${entityKey}.themeLabel`, {
        defaultValue: profileConfig.themeLabel,
      }),
      themeHint: t(`vouchers:profile.${entityKey}.themeHint`, {
        defaultValue: profileConfig.themeHint,
      }),

      // Free text
      descriptionLabel: t("vouchers:voucherPage.descriptionLabel"),
      descriptionPlaceholder: isPackageCategory
        ? t("vouchers:voucherPage.descriptionPlaceholderPackage")
        : t("vouchers:voucherPage.descriptionPlaceholderVoucher"),

      commentLabel: t("vouchers:voucherPage.commentLabel"),
      commentPlaceholder: isPackageCategory
        ? t("vouchers:voucherPage.commentPlaceholderPackage")
        : t("vouchers:voucherPage.commentPlaceholderVoucher"),

      // Exit / leave
      exitButton: t("vouchers:voucherPage.exitButton"),
      exitTitleEdit: t("vouchers:voucherPage.exitTitleEdit"),
      exitTitleDraft: t("vouchers:voucherPage.exitTitleDraft"),

      // Cancel action title
      cancelActionTitle: t("vouchers:voucherPage.cancelActionTitle", i18nVars),
    };
  }, [isEdit, isPackageCategory, profileConfig, t]);

  /* -------------------------------------------------------------------------
     Derived options: template versions from current business profile
     -------------------------------------------------------------------------
     Main split behavior:
     - package mode sees package-specific options
     - voucher mode sees voucher-specific options
  */
  const templateVersionOptions = useMemo(() => {
    return profileConfig.templateVersionOptions || [];
  }, [profileConfig]);

  /* -------------------------------------------------------------------------
     Derived options: themes from the CURRENT family registry
     -------------------------------------------------------------------------
     This is another important split improvement.
     The page no longer blindly uses voucher theme registry for package mode.
  */
  const themeOptions = useMemo(() => {
    return Object.entries(profileConfig.themeRegistry || {})
      .filter(([key]) => key !== "warm_legacy")
      .map(([key, meta]) => ({
        value: key,
        label: meta.label || key,
      }));
  }, [profileConfig]);

  /* -------------------------------------------------------------------------
     Helper: scroll to top of the card (not window)
  */
  const scrollContainerToTop = useCallback(() => {
    const el = pageRef.current;
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  /* -------------------------------------------------------------------------
     Navigation blocker hook
  */
  useNavigationBlocker(
    isBlocking,
    undefined,
    { warnOnHardUnload: true }
  );

  /* -------------------------------------------------------------------------
     Sticky action bar shadow: detect scroll state of the page container
  */
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const onScroll = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 8;
      setShowSticky(hasOverflow && el.scrollTop > 8);
    };

    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });

    const onResize = () => onScroll();
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  /* -------------------------------------------------------------------------
     Initial load: users + defaults + populate form for edit
     -------------------------------------------------------------------------
     Important:
     - in edit flow we infer business mode from loaded category
     - then normalize templateVersion against the correct business profile
     - theme normalization also goes through the correct family config
  */
  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      try {
        const response = await fetchUsers();
        if (!cancelled) setUsers(response || []);
      } catch (error) {
        console.error("[VoucherPage] ❌ Error fetching users:", error);
        if (!cancelled) setErrorMessage("Failed to load users.");
      }
    };

    loadUsers();

    const validityDays = getValidityDays();
    const defaultExpiration = formatDateForInputLocal(
      addDaysLocal(new Date(), validityDays)
    );

    if (voucher && isEdit) {
      const loadedCategory = voucher.category || "";
      const loadedMode = getBusinessModeFromCategory(loadedCategory);
      const loadedProfile = getProfileConfig(loadedMode);

      const normalizedType = normalizeTemplateType(
        voucher.template_type,
        loadedMode
      );

      const normalizedVersion = normalizeTemplateVersion(
        voucher.template_version,
        loadedMode
      );

      const dbThemeNormalized = voucher.theme
        ? normalizeThemeForMode(voucher.theme, loadedMode)
        : null;

      const derivedTheme = dbThemeNormalized
        ? dbThemeNormalized
        : deriveThemeFromTemplateVersion(normalizedVersion, loadedMode);

      const expDate = normalizeDateToInput(
        voucher.expiration_date,
        defaultExpiration
      );

      const legacyBuyerName = [voucher.customer_first_name, voucher.customer_last_name]
        .filter(Boolean)
        .join(" ")
        .trim();

      if (!cancelled) {
        setVoucherId(voucher.id ?? null);
        setVoucherNo(voucher.voucher_no || "");
        setVoucherSearchNo(voucher.voucher_search_no || "");

        setCategory(loadedCategory);
        setIssuerId(voucher.issuer_id != null ? String(voucher.issuer_id) : "");
        setType(voucher.type || "Usługa");

        setBuyerName(voucher.buyer_name || legacyBuyerName || "");
        setRecipientName(voucher.recipient_name || "");
        setBuyerPhone(voucher.buyer_phone || voucher.customer_phone || "");

        setValue(voucher.value ?? "");
        setCurrency(voucher.currency || "PLN");

        setTemplateType(normalizedType || loadedProfile.templateTypeDefault);
        setTemplateVersion(normalizedVersion);
        setTheme(derivedTheme || loadedProfile.themeDefault);

        setDescription(voucher.description || "");
        setExpirationDate(expDate);
        setToNote(Boolean(voucher.to_note));
        setComment(voucher.comment || "");

        setStep(2);
        setIsBlocking(false);
      }
    } else {
      if (!cancelled) {
        const createMode = BUSINESS_MODES.VOUCHER;
        const createProfile = getProfileConfig(createMode);

        setStep(1);
        setCurrency("PLN");
        setTemplateType(createProfile.templateTypeDefault);
        setTemplateVersion(createProfile.templateVersionDefault);
        setTheme(
          deriveThemeFromTemplateVersion(
            createProfile.templateVersionDefault,
            createMode
          )
        );
        setExpirationDate(defaultExpiration);

        setIsBlocking(false);
        setReused(false);
        setIdemKey("");
      }
    }

    return () => {
      cancelled = true;
    };
  }, [voucher, isEdit]);

  /* -------------------------------------------------------------------------
     Business profile sync on category change (create flow only)
     -------------------------------------------------------------------------
     This is an important behavior:
     when the user changes category in step 1 before draft generation,
     metadata defaults should follow the selected business mode.

     We intentionally do NOT force-reset metadata during edit flow,
     because in edit flow the loaded voucher/package data should remain authoritative.
  */
  useEffect(() => {
    if (isEdit) return;
    if (step !== 1) return;

    const profile = getProfileConfig(businessMode);
    const nextVersion = profile.templateVersionDefault;

    setTemplateType(profile.templateTypeDefault);
    setTemplateVersion(nextVersion);
    setTheme(deriveThemeFromTemplateVersion(nextVersion, businessMode));
  }, [businessMode, isEdit, step]);

  /* -------------------------------------------------------------------------
     Step 1: Generate voucher/package number (create Draft server-side)
  */
  const generateVoucherNo = async () => {
    if (generating || isSaving) return;

    try {
      setGenerating(true);
      setIsSaving(true);
      setErrorMessage("");
      setSuccessMessage("");

      if (!ALLOWED_CATEGORIES.includes(category)) {
        setErrorMessage("Please select a valid category.");
        scrollContainerToTop();
        return;
      }

      const key = genIdempotencyKey();
      setIdemKey(key);

      const response = await fetchNextVoucherNumber(category, {
        idempotencyKey: key,
      });

      setVoucherNo(response?.voucher_no || "");
      setVoucherSearchNo(response?.voucher_no || "");
      setVoucherId(response?.id ?? null);

      const wasReused = Boolean(response?.reused);
      setReused(wasReused);

      setStep(2);
      setSuccessMessage(
        wasReused ? uiText.generateSuccessReused : uiText.generateSuccessNew
      );

      setIsBlocking(true);
      scrollContainerToTop();
    } catch (error) {
      console.error("[VoucherPage] ❌ Error generating voucher number:", error);
      setErrorMessage(error?.response?.data?.message || uiText.generateError);
      scrollContainerToTop();
    } finally {
      setIsSaving(false);
      setGenerating(false);
    }
  };

  /* -------------------------------------------------------------------------
     When templateVersion changes:
     - update templateVersion
     - auto-adjust theme ONLY if user did not override theme manually
     - mapping is business-profile-aware
  */
  const handleTemplateVersionChange = (newVersion) => {
    const prevVersion = templateVersion;

    setTemplateVersion(newVersion);

    const newMappedTheme = deriveThemeFromTemplateVersion(
      newVersion,
      businessMode
    );

    setTheme((prevTheme) => {
      const prevMapped = deriveThemeFromTemplateVersion(
        prevVersion,
        businessMode
      );

      if (!prevTheme || prevTheme === prevMapped) {
        return newMappedTheme;
      }

      return prevTheme;
    });
  };

  /* -------------------------------------------------------------------------
     Step 2: Save (finalize Draft -> Active, or update existing voucher/package)
  */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    setErrorMessage("");
    setSuccessMessage("");

    if (!voucherId) {
      setErrorMessage(uiText.missingIdError);
      scrollContainerToTop();
      return;
    }

    if (!issuerId) {
      setErrorMessage("Issuer is required.");
      scrollContainerToTop();
      return;
    }

    if (isPackageCategory && !String(recipientName || "").trim()) {
      setErrorMessage(uiText.recipientNameRequiredError);
      scrollContainerToTop();
      return;
    }

    const num = Number(value);
    if (value === "" || value === null || !Number.isFinite(num) || num < 0) {
      setErrorMessage("Value must be a non-negative number.");
      scrollContainerToTop();
      return;
    }

    if (expirationDate && !/^\d{4}-\d{2}-\d{2}$/.test(String(expirationDate))) {
      setErrorMessage("Expiration date must be in YYYY-MM-DD format.");
      scrollContainerToTop();
      return;
    }

    setIsSaving(true);

    try {
      const normalizedTemplateType = normalizeTemplateType(
        templateType || profileConfig.templateTypeDefault,
        businessMode
      );

      const normalizedTemplateVersion = normalizeTemplateVersion(
        templateVersion || profileConfig.templateVersionDefault,
        businessMode
      );

      const normalizedTheme = normalizeThemeForMode(
        theme ||
          deriveThemeFromTemplateVersion(
            normalizedTemplateVersion,
            businessMode
          ),
        businessMode
      );

      const payload = {
        voucher_search_no: voucherSearchNo || null,

        issuer_id: Number(issuerId),
        type,

        buyer_name: buyerName || null,
        recipient_name: recipientName || buyerName || null,
        buyer_phone: buyerPhone || null,

        value: num,
        currency: currency || "PLN",

        template_type: normalizedTemplateType,
        template_version: normalizedTemplateVersion,
        theme: normalizedTheme,

        description: description || null,
        expiration_date: expirationDate || null,
        to_note: !!toNote,
        comment: comment || null,
      };

      await updateVoucher(voucherId, payload);

      setSuccessMessage(uiText.saveSuccess);
      resetFormAndExit();
    } catch (error) {
      console.error("[VoucherPage] ❌ Error saving voucher:", error);
      setErrorMessage(error?.response?.data?.message || uiText.saveError);
      scrollContainerToTop();
    } finally {
      setIsSaving(false);
    }
  };

  /* -------------------------------------------------------------------------
     Explicit cancel (Edit flow only): set status = Cancelled on server
  */
  const handleCancelVoucher = async () => {
    if (!voucherId || isSaving) return;

    const ok = window.confirm(uiText.cancelEntityConfirm);
    if (!ok) return;

    try {
      setIsSaving(true);
      await cancelVoucher(voucherId);
      setSuccessMessage(uiText.cancelEntitySuccess);
      resetFormAndExit();
    } catch (error) {
      console.error("[VoucherPage] ❌ Error cancelling voucher:", error);
      setErrorMessage(error?.response?.data?.message || uiText.cancelEntityError);
      scrollContainerToTop();
    } finally {
      setIsSaving(false);
    }
  };

  /* -------------------------------------------------------------------------
     Reset local state & navigate back to overview
     -------------------------------------------------------------------------
     IMPORTANT:
     reset now returns to voucher-mode defaults intentionally.

     Why voucher mode?
     - page starts from neutral create state
     - user may then choose category again
     - business profile will then resync from category selection in step 1
  */
  const resetFormAndExit = () => {
    const resetMode = BUSINESS_MODES.VOUCHER;
    const resetProfile = getProfileConfig(resetMode);
    const resetVersion = resetProfile.templateVersionDefault;

    setStep(1);
    setVoucherId(null);
    setVoucherNo("");
    setVoucherSearchNo("");

    setCategory("");
    setIssuerId("");
    setType("Usługa");

    setBuyerName("");
    setRecipientName("");
    setBuyerPhone("");

    setValue("");
    setCurrency("PLN");

    setTemplateType(resetProfile.templateTypeDefault);
    setTemplateVersion(resetVersion);
    setTheme(deriveThemeFromTemplateVersion(resetVersion, resetMode));

    setDescription("");
    setExpirationDate("");
    setToNote(false);
    setComment("");

    setIsBlocking(false);
    setReused(false);
    setIdemKey("");

    navigate(backToOverviewPath);
  };

  const exitPage = () => resetFormAndExit();

  /* -------------------------------------------------------------------------
     Render
  */
  return (
    <div
      className="voucher-page"
      ref={pageRef}
      role="main"
      aria-label={uiText.pageTitle}
      data-mode={isPackageCategory ? "package" : "voucher"}
    >
      <div className="voucher-page-header">
        <div className="voucher-page-header__title">
          <h1>{uiText.pageTitle}</h1>
        </div>

        <div className="voucher-page-header__actions">
          <Link className="vp-btn vp-btn--soft" to={backToOverviewPath}>
            {t("vouchers:common.backToOverview")}
          </Link>
        </div>
      </div>

      {reused && (
        <div className="info-chip" aria-live="polite">
          {uiText.reusedInfoChip}
        </div>
      )}

      {successMessage && <p className="success-message">{successMessage}</p>}
      {errorMessage && <p className="error-message">{errorMessage}</p>}

      {step === 1 ? (
        <div className="step-1">
          <h2>{uiText.step1Title}</h2>

          <label htmlFor="category">{uiText.step1SelectLabel}</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isSaving}
            aria-label={uiText.categoryFieldAriaLabel}
          >
            <option value="">{uiText.selectPlaceholder}</option>
            {ALLOWED_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`vouchers:categories.${c}`, { defaultValue: c })}
              </option>
            ))}
          </select>

          <button
            className="generate-button"
            onClick={generateVoucherNo}
            disabled={!category || isSaving || generating}
          >
            {isSaving ? uiText.generateInProgress : uiText.step1GenerateButton}
          </button>

          <div
            className="form-actions"
            style={{ justifyContent: "center", marginTop: 12 }}
          >
            <button
              type="button"
              className="cancel-button"
              onClick={exitPage}
              disabled={isSaving}
            >
              {uiText.exitButton}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="voucher-form">
          <h2>{uiText.step2Title}</h2>

          <div className="form-group">
            <label>{uiText.numberLabel}</label>
            <input type="text" value={voucherNo} readOnly aria-readonly="true" />
          </div>

          <div className="form-group">
            <label>{uiText.searchNumberLabel}</label>
            <input
              type="text"
              value={voucherSearchNo}
              onChange={(e) => setVoucherSearchNo(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{uiText.expirationDateLabel}</label>
            <input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{uiText.categoryLabel}</label>
            <input type="text" value={category} readOnly aria-readonly="true" />
          </div>

          <div className="form-group">
            <label>{uiText.issuerLabel}</label>
            <select
              value={issuerId}
              onChange={(e) => setIssuerId(e.target.value)}
              required
              disabled={isSaving}
            >
              <option value="">{uiText.selectIssuer}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.username})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{uiText.typeLabel}</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={isSaving}
            >
              <option value="Usługa">{t("vouchers:types.Usługa")}</option>
              <option value="Kwota">{t("vouchers:types.Kwota")}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{uiText.buyerNameLabel}</label>
            <input
              type="text"
              value={buyerName}
              onChange={(e) => setBuyerName(e.target.value)}
              placeholder={uiText.buyerNamePlaceholder}
              disabled={isSaving}
            />
          </div>

          <div className="form-group">
            <label className={isPackageCategory ? "is-required-label" : ""}>
              {uiText.recipientNameLabel}
              {isPackageCategory && (
                <span className="required-star" aria-hidden="true">
                  {" "}*
                </span>
              )}
            </label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder={uiText.recipientNamePlaceholder}
              disabled={isSaving}
              required={isPackageCategory}
              aria-required={isPackageCategory ? "true" : "false"}
              className={isPackageCategory ? "is-package-required-field" : ""}
            />
          </div>

          <div className="form-group">
            <label>{uiText.buyerPhoneLabel}</label>
            <input
              type="text"
              value={buyerPhone}
              onChange={(e) => setBuyerPhone(e.target.value)}
              disabled={isSaving}
            />
          </div>

          <div className="form-group value-with-checkbox">
            <div className="value-row">
              <label htmlFor="voucherValue" className="sr-only">
                {uiText.valueAriaLabel}
              </label>

              <input
                id="voucherValue"
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
                min="0"
                step="0.01"
                placeholder={uiText.valueAriaLabel}
                aria-label={uiText.valueAriaLabel}
                disabled={isSaving}
              />

              <span
                style={{
                  marginLeft: 8,
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                }}
              >
                {currency}
              </span>

              <label className="mark-to-note">
                <input
                  type="checkbox"
                  checked={toNote}
                  onChange={(e) => setToNote(e.target.checked)}
                  aria-label={uiText.markToNote}
                  disabled={isSaving}
                />
                {uiText.markToNote}
              </label>
            </div>
          </div>

          <div className="form-group form-group--full">
            <fieldset className="template-meta-fieldset" disabled={isSaving}>
              <legend>{uiText.layoutLegend}</legend>

              <div className="template-meta-row">
                <div className="template-meta-col">
                  <label htmlFor="templateType">{uiText.templateTypeLabel}</label>
                  <select
                    id="templateType"
                    value={templateType}
                    onChange={(e) => setTemplateType(e.target.value)}
                  >
                    {TEMPLATE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(`vouchers:templateTypes.${opt.value}.label`, {
                          defaultValue: opt.label,
                        })}
                      </option>
                    ))}
                  </select>
                  <small className="template-meta-hint">
                    {uiText.templateTypeHint}
                  </small>
                </div>

                <div className="template-meta-col">
                  <label htmlFor="templateVersion">
                    {uiText.templateVersionLabel}
                  </label>
                  <select
                    id="templateVersion"
                    value={templateVersion}
                    onChange={(e) => handleTemplateVersionChange(e.target.value)}
                  >
                    {templateVersionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(`vouchers:templateVersions.${opt.value}`, {
                          defaultValue: opt.label,
                        })}
                      </option>
                    ))}
                  </select>
                  <small className="template-meta-hint">
                    {uiText.templateVersionHint}
                  </small>
                </div>

                <div className="template-meta-col">
                  <label htmlFor="theme">{uiText.themeLabel}</label>
                  <select
                    id="theme"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                  >
                    {themeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <small className="template-meta-hint">
                    {uiText.themeHint}
                  </small>
                </div>
              </div>
            </fieldset>
          </div>

          <div className="form-group form-group--full">
            <label>{uiText.descriptionLabel}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={uiText.descriptionPlaceholder}
              disabled={isSaving}
            />
          </div>

          <div className="form-group form-group--full">
            <label>{uiText.commentLabel}</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={uiText.commentPlaceholder}
              rows={3}
              disabled={isSaving}
            />
          </div>

          <div className={`form-actions ${showSticky ? "is-sticky" : ""}`}>
            <button type="submit" disabled={isSaving}>
              {isEdit ? uiText.saveChangesButton : uiText.createButton}
            </button>

            <button
              type="button"
              className="cancel-button"
              onClick={exitPage}
              disabled={isSaving}
              title={isEdit ? uiText.exitTitleEdit : uiText.exitTitleDraft}
            >
              {uiText.exitButton}
            </button>

            {isEdit && (
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancelVoucher}
                disabled={isSaving}
                title={uiText.cancelActionTitle}
                style={{ background: "#8b0000" }}
              >
                {uiText.cancelEntityButton}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default VoucherPage;